import React, { useEffect, useState } from "react";
import { Card, Form, Button } from 'react-bootstrap';
import SecondaryButton from '../../../components/common/SecondaryButton';
import PrimaryButton from '../../../components/common/PrimaryButton';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import AutomationService from "../../../services/AutomationService";

const FollowUpTemplate = () => {

    const modules = {
        toolbar: [
            [{ 'header': [1, 2, false] }],
            ['bold', 'italic', 'underline', 'strike', 'blockquote'],
            [{'list': 'ordered'}, {'list': 'bullet'}, {'indent': '-1'}, {'indent': '+1'}],
            ['link', 'image'],
            ['clean']
        ],
    };

    const [templates, setTemplates] = useState([]);
    const [name, setName] = useState('');
    const [content, setContent] = useState('');
    const [showForm, setShowForm] = useState(false);
    const [defaultSelectedEmailTemplateId, setDefaultSelectedEmailTemplateId] = useState(null);
    const [showChooser, setShowChooser] = useState(false);
    const [defaultTemplates, setDefaultTemplates] = useState([]);
    const [loadingTemplates, setLoadingTemplates] = useState(false);
    const [selectedEmailTemplateId, setSelectedEmailTemplateId] = useState(null);

    const fetchTemplates = async () => {
        try {
            const response = await AutomationService.fetchAllEmailFollowUpTemplates();
            if (response.data?.success && response.data.data) {
                const fetchedTemplates = response.data.data.map(template => ({
                    id: template.id,
                    title: template.name,
                    emailFollowUpCategoryId: template.emailFollowUpCategoryId,
                    createdAt: new Date(template.createdOn).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                    }),
                    content: template.templateBody
                }));
                setTemplates(fetchedTemplates);
            } else {
                console.error('No templates available or failed to fetch templates:', response.message);
            }
        } catch (error) {
            console.error('Error fetching templates:', error);
        }
    };

    useEffect(() => {
        fetchTemplates();
    }, []);

    const handleSave = async () => {
        if (!name && !content) return;
        if (defaultSelectedEmailTemplateId) {
            // update existing
            setTemplates(prev => prev.map(t => t.id === defaultSelectedEmailTemplateId ? { ...t, title: name || 'Untitled', content } : t));
        } else {
            const newTemplate = {
                id: Date.now(),
                title: name || 'Untitled',
                createdAt: new Date().toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                }),
                content
            };
            setTemplates(prev => [newTemplate, ...prev]);
        }
        setName('');
        setContent('');
        setDefaultSelectedEmailTemplateId(null);
        setShowForm(false);
    };

    const handleDelete = (id) => {
        setTemplates(prev => prev.filter(t => t.id !== id));
    };

    const handleCopy = (text) => {
        if (!navigator.clipboard) return;
        navigator.clipboard.writeText(text);
    };

    const handleSelect = (t) => {
        setSelectedEmailTemplateId(t.id);
        setName(t.title);
        setContent(t.content);
        setDefaultSelectedEmailTemplateId(t.emailFollowUpCategoryId);
        setShowForm(true);
    };

    const handleAddClick = async () => {
        setLoadingTemplates(true);
        try {
            const response = await AutomationService.fetchAllDefaultEmailFollowUpTemplates();
            if (response.data?.success) {
                const mappedTemplates = response.data.data.map(template => ({
                    id: template.id,
                    title: template.name,
                    content: template.templateBody
                }));
                setDefaultTemplates(mappedTemplates);
            }
        } catch (error) {
            console.error('Error fetching default templates:', error);
        } finally {
            setLoadingTemplates(false);
            setShowChooser(true);
        }
    };

    const handleCancel = () => {
        setName('');
        setContent('');
        setDefaultSelectedEmailTemplateId(null);
        setShowForm(templates.length === 0);
    };

    const handleSelectTemplate = (template) => {
        setName(template.title);
        setContent(template.content);
        setDefaultSelectedEmailTemplateId(template.id);
        setShowForm(true);
        setShowChooser(false);
    };

    const handleCreateTemplate = async () => {
        if (!name || !content) {
            alert('Please provide both a name and content for the template.');
            return;
        }

        const payload = {
            emailFollowUpCategoryId: defaultSelectedEmailTemplateId || (defaultTemplates.length > 0 ? defaultTemplates[0].id : null), // Use selected default template ID if available
            name,
            templateBody: content
        };

        try {
            const response = await AutomationService.CreateEmailFollowUpTemplate(payload);
            if (response.data?.success) {
                alert('Template created successfully!');
                setTemplates(prev => [{
                    id: response.data.id,
                    title: response.data.name,
                    createdAt: new Date(response.data.createdOn).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                    }),
                    content: response.data.templateBody
                }, ...prev]);
                setName('');
                setContent('');
                setShowForm(false);
            } else {
                alert('Failed to create template: ' + response.message);
            }
        } catch (error) {
            console.error('Error creating template:', error);
            alert('An error occurred while creating the template.');
        } finally {
            // Refresh the template list after creation
            fetchTemplates();
        }
    };

    const handleAddEditEmailTemplate = () => {
        if(selectedEmailTemplateId){
            handleUpdateTemplate();
        }
        else{
            handleCreateTemplate();
        }
    }

    const handleUpdateTemplate = async () => {
        if (!name || !content) {
            alert('Please provide both a name and content for the template.');
            return;
        }

        const payload = {
            id: selectedEmailTemplateId,
            emailFollowUpCategoryId: defaultSelectedEmailTemplateId || (defaultTemplates.length > 0 ? defaultTemplates[0].id : null), // Use selected default template ID if available
            name,
            templateBody: content
        };

        try {
            const response = await AutomationService.UpdateEmailFollowUpTemplate(payload);
            if (response.data?.success) {
                alert('Template Updated successfully!');
                setTemplates(prev => [{
                    id: response.data.id,
                    title: response.data.name,
                    createdAt: new Date(response.data.createdOn).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                    }),
                    content: response.data.templateBody
                }, ...prev]);
                setName('');
                setContent('');
                setSelectedEmailTemplateId(null);
                setDefaultSelectedEmailTemplateId(null);
                setShowForm(false);
            } else {
                alert('Failed to update template: ' + response.message);
            }
        } catch (error) {
            console.error('Error updating template:', error);
            alert('An error occurred while updating the template.');
        } finally {
            // Refresh the template list after creation
            fetchTemplates();
        }
    };

    const createCardStyle = { backgroundColor: '#f3f8ff', borderRadius: 8, padding: 20, border: '1px solid #e3eeff' };
    const templateContentStyle = { backgroundColor: '#f7f7f9', padding: '12px 16px', borderRadius: 6 };

    return (
        <div>
            <div className="mb-4 d-flex justify-content-between align-items-start">
                <div>
                    <h3 className="fw-bold mb-1">Communication Templates</h3>
                    <p className="text-muted">Create reusable templates for email and WhatsApp messages</p>
                </div>
                <div>
                    {!showForm && !showChooser && <Button variant="primary" onClick={handleAddClick}>Add Template</Button>}
                </div>
            </div>

            {showChooser ? (
                <div className="mb-4">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <div>
                            <h5 className="mb-1 fw-bold">Select a Default Template</h5>
                            <p className="text-muted mb-0 small">Choose from our pre-built templates and customize as needed</p>
                        </div>
                        <SecondaryButton onClick={() => setShowChooser(false)}>Cancel</SecondaryButton>
                    </div>
                    {loadingTemplates ? (
                        <div className="text-center py-5">
                            <div className="spinner-border text-primary mb-3" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="text-muted">Loading templates...</p>
                        </div>
                    ) : defaultTemplates.length > 0 ? (
                        <div className="row g-3">
                            {defaultTemplates.map(t => (
                                <div className="col-md-6 col-lg-4" key={t.id}>
                                    <Card 
                                        className="h-100 border-0 shadow-sm cursor-pointer transition-all"
                                        style={{
                                            borderRadius: '8px',
                                            overflow: 'hidden',
                                            transition: 'all 0.3s ease',
                                            cursor: 'pointer',
                                            padding: '16px',
                                            margin: '8px',
                                            borderWidth: '1.5px',
                                            borderStyle: 'solid',
                                            borderColor: '#e3eeff',
                                            boxShadow: '0 6px 8px rgba(0, 0, 0, 0.1)'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.boxShadow = '0 8px 16px rgba(0, 0, 0, 0.15)';
                                            e.currentTarget.style.transform = 'translateY(-4px)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.boxShadow = '0 6px 8px rgba(0, 0, 0, 0.1)';
                                            e.currentTarget.style.transform = 'translateY(0)';
                                        }}
                                    >
                                        <div style={{ background: 'linear-gradient(135deg, #D07C3F 0%, #E8A366 100%)', height: '60px', marginBottom: '12px' }}></div>
                                        <Card.Body className="d-flex flex-column" style={{ minHeight: '220px', padding: '12px' }}>
                                            <h6 className="fw-bold mb-2 text-dark" style={{ fontSize: '1rem' }}>
                                                {t.title}
                                            </h6>
                                            <div 
                                                className="text-muted small flex-grow-1 mb-3"
                                                style={{
                                                    lineHeight: '1.5',
                                                    padding: '8px'
                                                }}
                                                dangerouslySetInnerHTML={{ __html: t.content }}
                                            ></div>
                                            <PrimaryButton 
                                                size="sm" 
                                                className="w-100"
                                                onClick={() => handleSelectTemplate(t)}
                                                style={{ borderRadius: '8px', marginTop: '12px' }}
                                            >
                                                <i className="bi bi-check-circle me-2"></i>
                                                Use Template
                                            </PrimaryButton>
                                        </Card.Body>
                                    </Card>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-5">
                            <p className="text-muted">No templates available</p>
                        </div>
                    )}
                </div>
            ) : showForm ? (
                <div style={createCardStyle} className="mb-4">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <h5 className="mb-0">{defaultSelectedEmailTemplateId ? 'Edit Template' : 'Create New Template'}</h5>
                        {templates.length > 0 && <SecondaryButton onClick={() => setShowForm(false)}>Back to list</SecondaryButton>}
                    </div>

                    <Form>
                        <Form.Group className="mb-3" controlId="templateName">
                            <Form.Label className="small text-muted">Template Name</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="e.g., Welcome Email, Follow-up Message"
                                value={name}
                                onChange={e => setName(e.target.value)}
                            />
                        </Form.Group>

                        <Form.Group className="mb-2" controlId="templateContent">
                            <Form.Label className="small text-muted">Template Content</Form.Label>
                            <ReactQuill
                                theme="snow"
                                value={content}
                                onChange={setContent}                        
                                modules={modules}                                
                                placeholder="Enter your template content here. You can include formatting, URLs, images, and dynamic placeholders like {name}, {company}, {status}"
                            />
                        </Form.Group>

                        <div className="mb-3 small text-muted">Tip: This template includes placeholders like {`{UserName}`}, {`{OtpCode}`}, {`{ExpiryMinutes}`}, {`{CompanyName}`}, {`{LeadName}`}, and others. Ensure to retain these placeholders as they will be dynamically replaced with actual values.</div>

                        <div>
                            <Button 
                                variant="primary" 
                                className="me-2" 
                                onClick={handleAddEditEmailTemplate}
                            >
                                {selectedEmailTemplateId ? 'Save Changes' : 'Save Template'}
                            </Button>
                            <Button variant="light" onClick={handleCancel}>Cancel</Button>
                        </div>
                    </Form>
                </div>
            ) : (
                <div>
                    {templates.map(t => (
                        <Card className="mb-3" key={t.id}>
                            <Card.Body>
                                <div className="d-flex justify-content-between align-items-start">
                                    <div>
                                        <div className="small text-muted">{t.title}</div>
                                        <div className="small text-muted mb-2">Created {t.createdAt}</div>
                                    </div>
                                    <div>
                                        <Button variant="link" className="text-primary p-0 me-2" title="Edit" onClick={() => handleSelect(t)}>✏️</Button>
                                        <Button variant="link" className="text-danger p-0" onClick={(e) => { e.stopPropagation(); handleDelete(t.id); }} title="Delete">🗑️</Button>
                                    </div>
                                </div>

                                <div style={{ ...templateContentStyle, cursor: 'pointer' }} className="my-3" onClick={() => handleSelect(t)} dangerouslySetInnerHTML={{ __html: t.content }}></div>

                                {/* <div>
                                    <Button size="sm" variant="success" className="me-2" onClick={(e) => { e.stopPropagation(); handleCopy(t.content); }}>Copy for WhatsApp</Button>
                                    <Button size="sm" variant="outline-primary" onClick={(e) => { e.stopPropagation(); handleCopy(t.content); }}>Copy for Email</Button>
                                </div> */}
                            </Card.Body>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
};

export default FollowUpTemplate;
